'use client';

import React, { useState, useCallback } from 'react';
import { useRouter } from 'next/navigation';
import { createClient } from '@/lib/supabase/client';
import { ArrowRight, ArrowLeft, Save, CheckCircle2, Upload, X, FileText, AlertCircle, Link2, RotateCcw } from 'lucide-react';
import Image from 'next/image';
import Link from 'next/link';

// ─── Types ───────────────────────────────────────────────────────────────────

interface FormData {
  // Section 1
  full_name: string;
  id_passport_number: string;
  date_of_birth: string;
  nationality: string;
  gender: string;
  phone_number: string;
  email: string;
  residential_address: string;
  province: string;
  city: string;
  // Section 2
  programme_applying_for: string;
  preferred_intake: string;
  preferred_study_mode: string;
  programme_level: string;
  applicant_type: string;
  // Section 3
  highest_qualification: string;
  institution_attended: string;
  year_completed: string;
  field_of_study: string;
  // Section 4
  current_occupation: string;
  employer_name: string;
  industry: string;
  years_work_experience: string;
  real_estate_experience: string;
  // Section 5
  works_in_real_estate: boolean;
  ppra_registered: boolean;
  ffc_number: string;
  areas_of_interest: string[];
  // Section 6
  why_reusa: string;
  real_estate_meaning: string;
  career_outcome: string;
  challenge_overcome: string;
  contribution_to_sa: string;
  // Section 7
  commitment_level: string;
  hours_per_week: string;
  financially_prepared: boolean;
  requires_payment_plan: boolean;
  application_purpose: string;
  support_needed: string;
  // Section 9
  declaration_accurate: boolean;
  declaration_contact_consent: boolean;
  declaration_no_guarantee: boolean;
  declaration_popia_consent: boolean;
}

interface UploadedFile {
  type: string;
  name: string;
  path: string;
  size: number;
}

const SECTIONS = [
  { id: 1, title: 'Personal Information', short: 'Personal' },
  { id: 2, title: 'Programme Interest', short: 'Programme' },
  { id: 3, title: 'Education Background', short: 'Education' },
  { id: 4, title: 'Professional Background', short: 'Professional' },
  { id: 5, title: 'Real Estate Experience', short: 'Experience' },
  { id: 6, title: 'Motivational Statement', short: 'Motivation' },
  { id: 7, title: 'Admissions Fit', short: 'Fit Assessment' },
  { id: 8, title: 'Document Uploads', short: 'Documents' },
  { id: 9, title: 'Declarations', short: 'Declarations' },
];

const PROGRAMMES = [
  'Property Development (NQF 7)',
  'Real Estate Investment (NQF 7)',
  'AI for Real Estate (NQF 6)',
  'Luxury Real Estate Sales (NQF 5)',
  'Property Finance (NQF 7)',
  'Estate Agency Practice (NQF 4)',
  'Commercial Property Management (NQF 6)',
  'Property Valuation (NQF 7)',
  'PropTech & Innovation (NQF 6)',
  'Executive Real Estate Leadership (NQF 8)',
];

const PROVINCES = [
  'Gauteng', 'Western Cape', 'KwaZulu-Natal', 'Eastern Cape',
  'Limpopo', 'Mpumalanga', 'North West', 'Free State', 'Northern Cape',
  'Outside South Africa',
];

const AREAS_OF_INTEREST = [
  'Sales', 'Rentals', 'Development', 'Investment', 'Property Management',
  'Commercial Property', 'PropTech', 'Valuation', 'Finance', 'Auctioneering',
];

const DOCUMENT_TYPES = [
  { key: 'id_passport', label: 'ID / Passport', required: true },
  { key: 'matric_certificate', label: 'Matric Certificate', required: true },
  { key: 'highest_qualification', label: 'Highest Qualification', required: false },
  { key: 'academic_transcript', label: 'Academic Transcript', required: false },
  { key: 'cv', label: 'Curriculum Vitae (CV)', required: false },
  { key: 'proof_of_address', label: 'Proof of Address', required: false },
  { key: 'motivation_letter', label: 'Motivation Letter (Optional)', required: false },
];

const emptyForm: FormData = {
  full_name: '', id_passport_number: '', date_of_birth: '', nationality: '',
  gender: '', phone_number: '', email: '', residential_address: '', province: '', city: '',
  programme_applying_for: '', preferred_intake: '', preferred_study_mode: '',
  programme_level: '', applicant_type: '',
  highest_qualification: '', institution_attended: '', year_completed: '', field_of_study: '',
  current_occupation: '', employer_name: '', industry: '', years_work_experience: '', real_estate_experience: '',
  works_in_real_estate: false, ppra_registered: false, ffc_number: '', areas_of_interest: [],
  why_reusa: '', real_estate_meaning: '', career_outcome: '', challenge_overcome: '', contribution_to_sa: '',
  commitment_level: '', hours_per_week: '', financially_prepared: false, requires_payment_plan: false,
  application_purpose: '', support_needed: '',
  declaration_accurate: false, declaration_contact_consent: false,
  declaration_no_guarantee: false, declaration_popia_consent: false,
};

// ─── Field Components ─────────────────────────────────────────────────────────

const Field = ({ label, required, children }: { label: string; required?: boolean; children: React.ReactNode }) => (
  <div className="flex flex-col gap-1.5">
    <label className="font-sans text-sm font-medium text-foreground">
      {label}{required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
  </div>
);

const inputCls = "w-full px-4 py-3 rounded-xl border border-border bg-background font-sans text-sm text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all duration-200";
const selectCls = `${inputCls} cursor-pointer`;
const textareaCls = `${inputCls} resize-none leading-relaxed`;

// ─── Main Component ───────────────────────────────────────────────────────────

export default function ApplicationForm() {
  const router = useRouter();
  const supabase = createClient();

  const [step, setStep] = useState(1);
  const [form, setForm] = useState<FormData>(emptyForm);
  const [uploadedFiles, setUploadedFiles] = useState<Record<string, UploadedFile>>({});
  const [uploading, setUploading] = useState<Record<string, boolean>>({});
  const [applicationId, setApplicationId] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [saveMessage, setSaveMessage] = useState('');
  const [error, setError] = useState('');
  const [showSaveSuccess, setShowSaveSuccess] = useState(false);
  const [resumeId, setResumeId] = useState('');
  const [resumeLoading, setResumeLoading] = useState(false);
  const [resumeError, setResumeError] = useState('');

  const set = (field: keyof FormData, value: any) =>
    setForm((prev) => ({ ...prev, [field]: value }));

  const toggleArea = (area: string) => {
    setForm((prev) => ({
      ...prev,
      areas_of_interest: prev.areas_of_interest.includes(area)
        ? prev.areas_of_interest.filter((a) => a !== area)
        : [...prev.areas_of_interest, area],
    }));
  };

  // Resume a saved application by ID
  const handleResume = async () => {
    if (!resumeId.trim()) return;
    setResumeLoading(true);
    setResumeError('');
    try {
      const { data, error: fetchError } = await supabase
        .from('applications')
        .select('*')
        .eq('id', resumeId.trim())
        .single();
      if (fetchError || !data) {
        setResumeError('Application not found. Please check your reference ID.');
        return;
      }
      // Restore form data
      const { id, status, applicant_email, submitted_at, created_at, updated_at, ...formFields } = data;
      setForm({ ...emptyForm, ...formFields });
      setApplicationId(id);
      setStep(1);
      setSaveMessage('Application loaded — continue where you left off.');
      setTimeout(() => setSaveMessage(''), 4000);
    } catch {
      setResumeError('Failed to load application. Please try again.');
    } finally {
      setResumeLoading(false);
    }
  };

  // Save draft to Supabase
  const saveDraft = useCallback(async () => {
    setSaving(true);
    setSaveMessage('');
    setShowSaveSuccess(false);
    try {
      const payload = { ...form, status: 'draft' as const, applicant_email: form.email || 'draft@reusa.education' };
      if (applicationId) {
        await supabase.from('applications').update(payload).eq('id', applicationId);
        setShowSaveSuccess(true);
        setSaveMessage('Progress saved');
      } else {
        const { data, error: insertError } = await supabase
          .from('applications')
          .insert(payload)
          .select('id')
          .single();
        if (insertError) throw insertError;
        if (data) {
          setApplicationId(data.id);
          setShowSaveSuccess(true);
          setSaveMessage('Progress saved');
        }
      }
      setTimeout(() => setSaveMessage(''), 5000);
    } catch (err: any) {
      setSaveMessage('Save failed');
    } finally {
      setSaving(false);
    }
  }, [form, applicationId, supabase]);

  // Upload file to Supabase Storage
  const handleFileUpload = async (docType: string, file: File) => {
    setUploading((prev) => ({ ...prev, [docType]: true }));
    try {
      const ext = file.name.split('.').pop();
      const path = `applications/${Date.now()}_${docType}.${ext}`;
      const { error: uploadError } = await supabase.storage
        .from('application-documents')
        .upload(path, file, { upsert: true });
      if (uploadError) throw uploadError;
      setUploadedFiles((prev) => ({
        ...prev,
        [docType]: { type: docType, name: file.name, path, size: file.size },
      }));
    } catch (err: any) {
      setError(`Upload failed: ${err.message}`);
    } finally {
      setUploading((prev) => ({ ...prev, [docType]: false }));
    }
  };

  const removeFile = (docType: string) => {
    setUploadedFiles((prev) => {
      const next = { ...prev };
      delete next[docType];
      return next;
    });
  };

  // Final submission
  const handleSubmit = async () => {
    if (!form.declaration_accurate || !form.declaration_contact_consent ||
        !form.declaration_no_guarantee || !form.declaration_popia_consent) {
      setError('Please accept all declarations before submitting.');
      return;
    }
    setSubmitting(true);
    setError('');
    try {
      const payload = {
        ...form,
        status: 'submitted' as const,
        applicant_email: form.email,
        submitted_at: new Date().toISOString(),
      };

      let appId = applicationId;
      if (appId) {
        await supabase.from('applications').update(payload).eq('id', appId);
      } else {
        const { data, error: insertError } = await supabase
          .from('applications')
          .insert(payload)
          .select('id')
          .single();
        if (insertError) throw insertError;
        appId = data?.id;
      }

      // Save document records
      if (appId) {
        const docInserts = Object.values(uploadedFiles).map((f) => ({
          application_id: appId,
          document_type: f.type,
          file_name: f.name,
          file_path: f.path,
          file_size: f.size,
        }));
        if (docInserts.length > 0) {
          await supabase.from('application_documents').insert(docInserts);
        }

        // Send emails via edge function
        try {
          await supabase.functions.invoke('send-admission-email', {
            body: {
              type: 'applicant_confirmation',
              applicantEmail: form.email,
              applicantName: form.full_name,
              programme: form.programme_applying_for,
              applicationId: appId,
            },
          });
          await supabase.functions.invoke('send-admission-email', {
            body: {
              type: 'admin_notification',
              applicantEmail: form.email,
              applicantName: form.full_name,
              programme: form.programme_applying_for,
              applicationId: appId,
            },
          });
        } catch {
          // Email errors are non-blocking
        }
      }

      router.push(`/apply/thank-you?name=${encodeURIComponent(form.full_name)}`);
    } catch (err: any) {
      setError(err.message || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const nextStep = () => { setStep((s) => Math.min(s + 1, 9)); window.scrollTo({ top: 0, behavior: 'smooth' }); };
  const prevStep = () => { setStep((s) => Math.max(s - 1, 1)); window.scrollTo({ top: 0, behavior: 'smooth' }); };

  const progress = ((step - 1) / (SECTIONS.length - 1)) * 100;

  return (
    <div className="min-h-screen bg-secondary">
      {/* ── Header ── */}
      <div className="bg-primary text-white">
        <div className="max-w-4xl mx-auto px-6 py-8">
          {/* Logo top-left */}
          <div className="mb-5">
            <Link href="/" className="inline-flex items-center gap-2 hover:opacity-80 transition-opacity" aria-label="REUSA Home">
              <Image
                src="/assets/images/REUSA_PNG_LOGO_2_-1779294693104.png"
                alt="REUSA logo"
                width={80}
                height={28}
                className="object-contain brightness-0 invert"
                priority
              />
            </Link>
          </div>
          <div className="flex items-center gap-3 mb-2">
            <div className="w-px h-6 bg-accent" />
            <span className="font-sans text-xs tracking-[0.2em] text-accent uppercase">Admissions Portal</span>
          </div>
          <h1 className="font-serif text-2xl lg:text-3xl text-white mb-1">Application for Admission</h1>
          <p className="font-sans text-sm text-white/60">Real Estate University of South Africa · 2026 Intake</p>
        </div>
      </div>

      {/* ── Resume Banner ── */}
      {!applicationId && (
        <div className="bg-white border-b border-border">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3">
              <div className="flex items-center gap-2 text-muted-foreground flex-shrink-0">
                <RotateCcw size={14} />
                <span className="font-sans text-xs font-medium">Returning applicant?</span>
              </div>
              <div className="flex items-center gap-2 flex-1 w-full sm:w-auto">
                <input
                  type="text"
                  placeholder="Enter your reference ID to resume…"
                  value={resumeId}
                  onChange={(e) => setResumeId(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && handleResume()}
                  className="flex-1 px-3 py-1.5 rounded-lg border border-border font-sans text-xs text-foreground placeholder:text-muted-foreground/50 focus:outline-none focus:ring-2 focus:ring-primary/30 focus:border-primary transition-all"
                />
                <button
                  onClick={handleResume}
                  disabled={resumeLoading || !resumeId.trim()}
                  className="px-4 py-1.5 rounded-lg bg-primary text-white font-sans text-xs font-semibold hover:bg-primary/90 transition-all disabled:opacity-50 flex-shrink-0"
                >
                  {resumeLoading ? 'Loading…' : 'Resume'}
                </button>
              </div>
              {resumeError && <p className="font-sans text-xs text-red-500">{resumeError}</p>}
            </div>
          </div>
        </div>
      )}

      {/* ── Save Success Banner ── */}
      {showSaveSuccess && applicationId && (
        <div className="bg-green-50 border-b border-green-200">
          <div className="max-w-4xl mx-auto px-6 py-3">
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2">
              <div className="flex items-center gap-2">
                <CheckCircle2 size={14} className="text-green-600 flex-shrink-0" />
                <span className="font-sans text-xs font-semibold text-green-800">Progress saved!</span>
              </div>
              <div className="flex items-center gap-2">
                <span className="font-sans text-xs text-green-700">Your reference ID:</span>
                <code className="font-mono text-xs font-bold text-green-900 bg-green-100 px-2 py-0.5 rounded select-all">{applicationId}</code>
                <button
                  onClick={() => { navigator.clipboard?.writeText(applicationId); }}
                  className="flex items-center gap-1 font-sans text-xs text-green-700 hover:text-green-900 transition-colors"
                  title="Copy reference ID"
                >
                  <Link2 size={12} /> Copy
                </button>
              </div>
              <span className="font-sans text-xs text-green-600">Use this ID to return and continue your application.</span>
            </div>
          </div>
        </div>
      )}

      {/* ── Progress Bar ── */}
      <div className="bg-white border-b border-border sticky top-0 z-30 shadow-sm">
        <div className="max-w-4xl mx-auto px-6 py-4">
          {/* Step labels — desktop */}
          <div className="hidden lg:flex items-center justify-between mb-3">
            {SECTIONS.map((s) => (
              <button
                key={s.id}
                onClick={() => setStep(s.id)}
                className={`font-sans text-xs font-medium transition-colors ${
                  s.id === step ? 'text-primary' : s.id < step ? 'text-accent' : 'text-muted-foreground/50'
                }`}
              >
                {s.short}
              </button>
            ))}
          </div>
          {/* Mobile step label */}
          <div className="lg:hidden flex items-center justify-between mb-2">
            <span className="font-sans text-sm font-semibold text-primary">
              Step {step} of {SECTIONS.length}: {SECTIONS[step - 1]?.title}
            </span>
            <span className="font-sans text-xs text-muted-foreground">{Math.round(progress)}%</span>
          </div>
          {/* Progress track */}
          <div className="relative h-1.5 bg-border rounded-full overflow-hidden">
            <div
              className="absolute left-0 top-0 h-full rounded-full transition-all duration-500"
              style={{ width: `${progress}%`, background: 'linear-gradient(90deg, var(--primary), var(--accent))' }}
            />
          </div>
          {/* Step dots — desktop */}
          <div className="hidden lg:flex items-center justify-between mt-2">
            {SECTIONS.map((s) => (
              <div
                key={s.id}
                className={`w-2 h-2 rounded-full transition-all duration-300 ${
                  s.id < step ? 'bg-accent' : s.id === step ? 'bg-primary scale-125' : 'bg-border'
                }`}
              />
            ))}
          </div>
        </div>
      </div>

      {/* ── Form Body ── */}
      <div className="max-w-4xl mx-auto px-6 py-10">
        <div className="bg-white rounded-2xl border border-border shadow-sm overflow-hidden">
          {/* Section header */}
          <div className="px-8 py-6 border-b border-border bg-secondary/30">
            <div className="flex items-center gap-3">
              <span className="w-8 h-8 rounded-lg bg-primary text-white font-sans text-sm font-bold flex items-center justify-center flex-shrink-0">
                {step}
              </span>
              <div>
                <h2 className="font-serif text-xl text-foreground">{SECTIONS[step - 1]?.title}</h2>
                <p className="font-sans text-xs text-muted-foreground mt-0.5">Section {step} of {SECTIONS.length}</p>
              </div>
            </div>
          </div>

          <div className="px-8 py-8">
            {/* ── Section 1: Personal Information ── */}
            {step === 1 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Field label="Full Name (as per ID/Passport)" required>
                    <input className={inputCls} placeholder="e.g. Nomvula Thandi Dlamini" value={form.full_name} onChange={(e) => set('full_name', e.target.value)} />
                  </Field>
                </div>
                <Field label="ID / Passport Number" required>
                  <input className={inputCls} placeholder="South African ID or passport number" value={form.id_passport_number} onChange={(e) => set('id_passport_number', e.target.value)} />
                </Field>
                <Field label="Date of Birth" required>
                  <input type="date" className={inputCls} value={form.date_of_birth} onChange={(e) => set('date_of_birth', e.target.value)} />
                </Field>
                <Field label="Nationality" required>
                  <input className={inputCls} placeholder="e.g. South African" value={form.nationality} onChange={(e) => set('nationality', e.target.value)} />
                </Field>
                <Field label="Gender">
                  <select className={selectCls} value={form.gender} onChange={(e) => set('gender', e.target.value)}>
                    <option value="">Select gender</option>
                    <option>Male</option><option>Female</option><option>Non-binary</option><option>Prefer not to say</option>
                  </select>
                </Field>
                <Field label="Phone Number" required>
                  <input className={inputCls} placeholder="+27 82 000 0000" value={form.phone_number} onChange={(e) => set('phone_number', e.target.value)} />
                </Field>
                <Field label="Email Address" required>
                  <input type="email" className={inputCls} placeholder="your@email.com" value={form.email} onChange={(e) => set('email', e.target.value)} />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Residential Address" required>
                    <input className={inputCls} placeholder="Street address" value={form.residential_address} onChange={(e) => set('residential_address', e.target.value)} />
                  </Field>
                </div>
                <Field label="Province" required>
                  <select className={selectCls} value={form.province} onChange={(e) => set('province', e.target.value)}>
                    <option value="">Select province</option>
                    {PROVINCES.map((p) => <option key={p}>{p}</option>)}
                  </select>
                </Field>
                <Field label="City / Town" required>
                  <input className={inputCls} placeholder="e.g. Johannesburg" value={form.city} onChange={(e) => set('city', e.target.value)} />
                </Field>
              </div>
            )}

            {/* ── Section 2: Programme Interest ── */}
            {step === 2 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Field label="Programme Applying For" required>
                    <select className={selectCls} value={form.programme_applying_for} onChange={(e) => set('programme_applying_for', e.target.value)}>
                      <option value="">Select a programme</option>
                      {PROGRAMMES.map((p) => <option key={p}>{p}</option>)}
                    </select>
                  </Field>
                </div>
                <Field label="Preferred Intake" required>
                  <select className={selectCls} value={form.preferred_intake} onChange={(e) => set('preferred_intake', e.target.value)}>
                    <option value="">Select intake</option>
                    <option>Q1 2026 (January)</option><option>Q2 2026 (April)</option>
                    <option>Q3 2026 (July)</option><option>Q4 2026 (October)</option>
                  </select>
                </Field>
                <Field label="Preferred Study Mode" required>
                  <select className={selectCls} value={form.preferred_study_mode} onChange={(e) => set('preferred_study_mode', e.target.value)}>
                    <option value="">Select mode</option>
                    <option value="online">Online</option>
                    <option value="hybrid">Hybrid (Online + In-Person)</option>
                    <option value="in_person">In-Person</option>
                  </select>
                </Field>
                <Field label="Programme Level" required>
                  <select className={selectCls} value={form.programme_level} onChange={(e) => set('programme_level', e.target.value)}>
                    <option value="">Select level</option>
                    <option value="certificate">Certificate</option>
                    <option value="diploma">Diploma</option>
                    <option value="short_course">Short Course</option>
                    <option value="executive_programme">Executive Programme</option>
                  </select>
                </Field>
                <Field label="I am applying as" required>
                  <select className={selectCls} value={form.applicant_type} onChange={(e) => set('applicant_type', e.target.value)}>
                    <option value="">Select applicant type</option>
                    <option value="student">Student</option>
                    <option value="working_professional">Working Professional</option>
                    <option value="real_estate_agent">Real Estate Agent</option>
                    <option value="entrepreneur">Entrepreneur</option>
                    <option value="investor">Investor</option>
                    <option value="other">Other</option>
                  </select>
                </Field>
              </div>
            )}

            {/* ── Section 3: Education Background ── */}
            {step === 3 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="md:col-span-2">
                  <Field label="Highest Qualification Completed" required>
                    <select className={selectCls} value={form.highest_qualification} onChange={(e) => set('highest_qualification', e.target.value)}>
                      <option value="">Select qualification</option>
                      <option>Grade 12 / Matric</option><option>Certificate (NQF 4–5)</option>
                      <option>Diploma (NQF 6)</option><option>Bachelor's Degree (NQF 7)</option>
                      <option>Honours Degree (NQF 8)</option><option>Master's Degree (NQF 9)</option>
                      <option>Doctoral Degree (NQF 10)</option><option>Other</option>
                    </select>
                  </Field>
                </div>
                <Field label="Institution Attended" required>
                  <input className={inputCls} placeholder="University, college, or school name" value={form.institution_attended} onChange={(e) => set('institution_attended', e.target.value)} />
                </Field>
                <Field label="Year Completed" required>
                  <input className={inputCls} placeholder="e.g. 2019" value={form.year_completed} onChange={(e) => set('year_completed', e.target.value)} />
                </Field>
                <div className="md:col-span-2">
                  <Field label="Subjects or Field of Study">
                    <input className={inputCls} placeholder="e.g. Business Management, Accounting, Property Studies" value={form.field_of_study} onChange={(e) => set('field_of_study', e.target.value)} />
                  </Field>
                </div>
                <div className="md:col-span-2 p-4 rounded-xl bg-secondary/50 border border-border">
                  <p className="font-sans text-sm text-muted-foreground">
                    <span className="font-semibold text-foreground">Academic records upload</span> — You will be able to upload your academic certificates and transcripts in Section 8 (Document Uploads).
                  </p>
                </div>
              </div>
            )}

            {/* ── Section 4: Professional Background ── */}
            {step === 4 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="Current Occupation / Job Title" required>
                  <input className={inputCls} placeholder="e.g. Property Manager" value={form.current_occupation} onChange={(e) => set('current_occupation', e.target.value)} />
                </Field>
                <Field label="Employer / Business Name">
                  <input className={inputCls} placeholder="Company or business name" value={form.employer_name} onChange={(e) => set('employer_name', e.target.value)} />
                </Field>
                <Field label="Industry / Sector">
                  <input className={inputCls} placeholder="e.g. Real Estate, Finance, Construction" value={form.industry} onChange={(e) => set('industry', e.target.value)} />
                </Field>
                <Field label="Years of Work Experience" required>
                  <select className={selectCls} value={form.years_work_experience} onChange={(e) => set('years_work_experience', e.target.value)}>
                    <option value="">Select range</option>
                    <option>Less than 1 year</option><option>1–3 years</option>
                    <option>3–5 years</option><option>5–10 years</option><option>10+ years</option>
                  </select>
                </Field>
                <div className="md:col-span-2">
                  <Field label="Real Estate / Property Experience">
                    <textarea className={textareaCls} rows={3} placeholder="Briefly describe any property-related experience, even if informal or investment-related." value={form.real_estate_experience} onChange={(e) => set('real_estate_experience', e.target.value)} />
                  </Field>
                </div>
              </div>
            )}

            {/* ── Section 5: Real Estate Experience ── */}
            {step === 5 && (
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-secondary/30">
                    <input type="checkbox" id="works_re" className="mt-1 w-4 h-4 accent-primary" checked={form.works_in_real_estate} onChange={(e) => set('works_in_real_estate', e.target.checked)} />
                    <label htmlFor="works_re" className="font-sans text-sm text-foreground cursor-pointer">I currently work in the real estate industry</label>
                  </div>
                  <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-secondary/30">
                    <input type="checkbox" id="ppra" className="mt-1 w-4 h-4 accent-primary" checked={form.ppra_registered} onChange={(e) => set('ppra_registered', e.target.checked)} />
                    <label htmlFor="ppra" className="font-sans text-sm text-foreground cursor-pointer">I am registered with the PPRA (Property Practitioners Regulatory Authority)</label>
                  </div>
                </div>
                {form.ppra_registered && (
                  <Field label="FFC Number (Fidelity Fund Certificate)">
                    <input className={inputCls} placeholder="Your FFC number" value={form.ffc_number} onChange={(e) => set('ffc_number', e.target.value)} />
                  </Field>
                )}
                <div>
                  <p className="font-sans text-sm font-medium text-foreground mb-3">Areas of Interest <span className="text-muted-foreground font-normal">(select all that apply)</span></p>
                  <div className="flex flex-wrap gap-2">
                    {AREAS_OF_INTEREST.map((area) => (
                      <button
                        key={area}
                        type="button"
                        onClick={() => toggleArea(area)}
                        className={`px-4 py-2 rounded-full font-sans text-sm font-medium border transition-all duration-200 ${
                          form.areas_of_interest.includes(area)
                            ? 'bg-primary text-white border-primary' :'bg-white text-muted-foreground border-border hover:border-primary hover:text-primary'
                        }`}
                      >
                        {area}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* ── Section 6: Motivational Statement ── */}
            {step === 6 && (
              <div className="space-y-7">
                <div className="p-5 rounded-xl bg-primary/5 border border-primary/20">
                  <p className="font-sans text-sm text-primary/80 leading-relaxed italic">
                    This section is your opportunity to speak directly to the REUSA Admissions Committee. Take your time. Write thoughtfully. These answers carry significant weight in our review process.
                  </p>
                </div>
                <Field label="Why do you want to study at REUSA?" required>
                  <textarea className={textareaCls} rows={5} placeholder="What draws you to REUSA specifically? What have you learned about our approach, faculty, or programmes that resonates with your goals?" value={form.why_reusa} onChange={(e) => set('why_reusa', e.target.value)} />
                  <span className="font-sans text-xs text-muted-foreground">{form.why_reusa.length} characters</span>
                </Field>
                <Field label="What does real estate mean to you?" required>
                  <textarea className={textareaCls} rows={4} placeholder="Beyond a career — what is your personal relationship with property, land, and the built environment?" value={form.real_estate_meaning} onChange={(e) => set('real_estate_meaning', e.target.value)} />
                </Field>
                <Field label="What career or business outcome are you hoping to achieve?" required>
                  <textarea className={textareaCls} rows={4} placeholder="Be specific. Where do you see yourself in 3–5 years? What does success look like for you?" value={form.career_outcome} onChange={(e) => set('career_outcome', e.target.value)} />
                </Field>
                <Field label="Describe a challenge you have overcome and what it taught you." required>
                  <textarea className={textareaCls} rows={5} placeholder="This can be personal, professional, or academic. We are interested in your resilience, self-awareness, and capacity to grow." value={form.challenge_overcome} onChange={(e) => set('challenge_overcome', e.target.value)} />
                </Field>
                <Field label="How do you believe REUSA can help you contribute to the future of South Africa's property sector?" required>
                  <textarea className={textareaCls} rows={5} placeholder="Think beyond your own career. How does your success connect to the broader transformation of South Africa's property landscape?" value={form.contribution_to_sa} onChange={(e) => set('contribution_to_sa', e.target.value)} />
                </Field>
              </div>
            )}

            {/* ── Section 7: Admissions Fit ── */}
            {step === 7 && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Field label="How committed are you to completing the programme?" required>
                  <select className={selectCls} value={form.commitment_level} onChange={(e) => set('commitment_level', e.target.value)}>
                    <option value="">Select level</option>
                    <option>Fully committed — this is my top priority</option>
                    <option>Very committed — I will make it work</option>
                    <option>Committed — with some flexibility needed</option>
                    <option>Exploring — not yet decided</option>
                  </select>
                </Field>
                <Field label="How many hours per week can you dedicate to studying?" required>
                  <select className={selectCls} value={form.hours_per_week} onChange={(e) => set('hours_per_week', e.target.value)}>
                    <option value="">Select hours</option>
                    <option>Less than 5 hours</option><option>5–10 hours</option>
                    <option>10–15 hours</option><option>15–20 hours</option><option>20+ hours</option>
                  </select>
                </Field>
                <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-secondary/30">
                  <input type="checkbox" id="fin_prep" className="mt-1 w-4 h-4 accent-primary" checked={form.financially_prepared} onChange={(e) => set('financially_prepared', e.target.checked)} />
                  <label htmlFor="fin_prep" className="font-sans text-sm text-foreground cursor-pointer">I am financially prepared for tuition fees</label>
                </div>
                <div className="flex items-start gap-3 p-4 rounded-xl border border-border bg-secondary/30">
                  <input type="checkbox" id="pay_plan" className="mt-1 w-4 h-4 accent-primary" checked={form.requires_payment_plan} onChange={(e) => set('requires_payment_plan', e.target.checked)} />
                  <label htmlFor="pay_plan" className="font-sans text-sm text-foreground cursor-pointer">I will require a payment plan or financing assistance</label>
                </div>
                <div className="md:col-span-2">
                  <Field label="I am applying primarily for" required>
                    <select className={selectCls} value={form.application_purpose} onChange={(e) => set('application_purpose', e.target.value)}>
                      <option value="">Select purpose</option>
                      <option>Personal development and enrichment</option>
                      <option>Career advancement in my current field</option>
                      <option>Transitioning into real estate</option>
                      <option>Growing my own property business</option>
                      <option>Obtaining a formal qualification</option>
                      <option>Multiple of the above</option>
                    </select>
                  </Field>
                </div>
                <div className="md:col-span-2">
                  <Field label="What support would help you succeed in your studies?">
                    <textarea className={textareaCls} rows={3} placeholder="e.g. flexible scheduling, mentorship, financial guidance, study groups, technical support..." value={form.support_needed} onChange={(e) => set('support_needed', e.target.value)} />
                  </Field>
                </div>
              </div>
            )}

            {/* ── Section 8: Document Uploads ── */}
            {step === 8 && (
              <div className="space-y-4">
                <p className="font-sans text-sm text-muted-foreground leading-relaxed mb-6">
                  Upload your supporting documents below. Accepted formats: PDF, JPG, PNG (max 10MB per file). Documents marked with <span className="text-red-500">*</span> are required for a complete application.
                </p>
                {DOCUMENT_TYPES.map((doc) => {
                  const uploaded = uploadedFiles[doc.key];
                  const isUploading = uploading[doc.key];
                  return (
                    <div key={doc.key} className="flex items-center gap-4 p-4 rounded-xl border border-border bg-secondary/20 hover:bg-secondary/40 transition-colors">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <FileText size={18} className="text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="font-sans text-sm font-medium text-foreground">
                          {doc.label}{doc.required && <span className="text-red-500 ml-1">*</span>}
                        </p>
                        {uploaded && (
                          <p className="font-sans text-xs text-muted-foreground truncate mt-0.5">{uploaded.name}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {uploaded ? (
                          <>
                            <CheckCircle2 size={16} className="text-green-600" />
                            <button onClick={() => removeFile(doc.key)} className="p-1 rounded hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors">
                              <X size={14} />
                            </button>
                          </>
                        ) : (
                          <label className={`flex items-center gap-2 px-4 py-2 rounded-lg font-sans text-xs font-semibold cursor-pointer transition-all ${isUploading ? 'bg-secondary text-muted-foreground' : 'bg-primary text-white hover:bg-primary/90'}`}>
                            {isUploading ? 'Uploading...' : <><Upload size={13} /> Upload</>}
                            <input
                              type="file"
                              className="hidden"
                              accept=".pdf,.jpg,.jpeg,.png"
                              disabled={isUploading}
                              onChange={(e) => {
                                const file = e.target.files?.[0];
                                if (file) handleFileUpload(doc.key, file);
                              }}
                            />
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {/* ── Section 9: Declarations ── */}
            {step === 9 && (
              <div className="space-y-6">
                <div className="p-5 rounded-xl bg-primary/5 border border-primary/20 mb-6">
                  <p className="font-sans text-sm text-primary/80 leading-relaxed">
                    Please read each declaration carefully and confirm your agreement before submitting your application.
                  </p>
                </div>

                {/* Review summary */}
                <div className="bg-secondary/50 rounded-xl p-6 border border-border mb-6">
                  <h3 className="font-serif text-lg text-foreground mb-4">Application Summary</h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
                    {[
                      ['Applicant', form.full_name || '—'],
                      ['Email', form.email || '—'],
                      ['Programme', form.programme_applying_for || '—'],
                      ['Intake', form.preferred_intake || '—'],
                      ['Study Mode', form.preferred_study_mode || '—'],
                      ['Qualification', form.highest_qualification || '—'],
                      ['Documents Uploaded', `${Object.keys(uploadedFiles).length} of ${DOCUMENT_TYPES.length}`],
                    ].map(([label, value]) => (
                      <div key={label} className="flex gap-2">
                        <span className="text-muted-foreground min-w-[120px]">{label}:</span>
                        <span className="font-medium text-foreground">{value}</span>
                      </div>
                    ))}
                  </div>
                </div>

                {[
                  { key: 'declaration_accurate', text: 'I confirm that all information provided in this application is true, accurate, and complete to the best of my knowledge.' },
                  { key: 'declaration_contact_consent', text: 'I consent to REUSA contacting me via email, phone, or other means regarding my application and related matters.' },
                  { key: 'declaration_no_guarantee', text: 'I understand that submission of this application does not guarantee admission to REUSA or any of its programmes.' },
                  { key: 'declaration_popia_consent', text: 'I consent to REUSA collecting, processing, and storing my personal information in accordance with the Protection of Personal Information Act (POPIA).' },
                ].map((decl) => (
                  <div key={decl.key} className="flex items-start gap-4 p-5 rounded-xl border border-border hover:border-primary/30 transition-colors">
                    <input
                      type="checkbox"
                      id={decl.key}
                      className="mt-1 w-5 h-5 accent-primary flex-shrink-0 cursor-pointer"
                      checked={form[decl.key as keyof FormData] as boolean}
                      onChange={(e) => set(decl.key as keyof FormData, e.target.checked)}
                    />
                    <label htmlFor={decl.key} className="font-sans text-sm text-foreground leading-relaxed cursor-pointer">
                      {decl.text}
                    </label>
                  </div>
                ))}

                {error && (
                  <div className="flex items-start gap-3 p-4 rounded-xl bg-red-50 border border-red-200">
                    <AlertCircle size={16} className="text-red-500 mt-0.5 flex-shrink-0" />
                    <p className="font-sans text-sm text-red-700">{error}</p>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Navigation Footer ── */}
          <div className="px-8 py-6 border-t border-border bg-secondary/20 flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              {step > 1 && (
                <button
                  onClick={prevStep}
                  className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border font-sans text-sm font-medium text-foreground hover:bg-secondary transition-all"
                >
                  <ArrowLeft size={15} /> Previous
                </button>
              )}
              <button
                onClick={saveDraft}
                disabled={saving}
                className="flex items-center gap-2 px-5 py-2.5 rounded-xl border border-border font-sans text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
              >
                <Save size={14} />
                {saving ? 'Saving...' : 'Save Progress'}
              </button>
              {saveMessage && (
                <span className={`font-sans text-xs ${saveMessage.includes('failed') ? 'text-red-500' : 'text-green-600'}`}>
                  {saveMessage}
                </span>
              )}
            </div>

            {step < 9 ? (
              <button
                onClick={nextStep}
                className="flex items-center gap-2 px-8 py-2.5 rounded-xl bg-primary text-white font-sans text-sm font-semibold hover:bg-primary/90 transition-all shadow-sm"
              >
                Continue <ArrowRight size={15} />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={submitting}
                className="flex items-center gap-2 px-10 py-3 rounded-xl font-sans text-sm font-semibold transition-all shadow-sm disabled:opacity-60"
                style={{ background: 'linear-gradient(135deg, #c9a84c, #e8c96a)', color: '#1a2b22' }}
              >
                {submitting ? 'Submitting...' : <>Submit Application <CheckCircle2 size={16} /></>}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
